import {
  createContractManagementCalendar,
  deleteContractManagementCalendar,
  getContractManagementCalendar,
  updateContractManagementCalendar,
  searchContractManagementCalendars,
  addCalendarReceivers,
  removeCalendarReceivers,
  listCalendarReceivers,
  addCalendarPermissionUsers,
  removeCalendarPermissionUsers,
  listCalendarPermissionUsers,
  addCalendarPermissionRoles,
  removeCalendarPermissionRoles,
  listCalendarPermissionRoles,
  addCalendarSourceDirectories,
  removeCalendarSourceDirectories,
  listCalendarSourceDirectories,
  addCalendarSourceContracts,
  removeCalendarSourceContracts,
  listCalendarSourceContracts,
  previewContractManagementCalendar,
  previewContractManagementCalendarRange,
  getContractManagementCalendarExportUrl,
  regenerateContractManagementCalendarExportUrl,
  type ContractManagementCalendarInDb,
  type ContractManagementCalendarCreate,
  type ContractManagementCalendarUpdate,
  type ContractManagementCalendarLinkResult,
  type ContractManagementContractInDbSlim,
  type CalendarPreviewQuery,
  type CalendarPreviewResult,
  type CalendarRangeQuery,
  type FilterableSearchQuery,
  type PermissionRoleInDb,
  type UserInDb,
  type VaultDirectoryInDbSlim,
} from '@/api';
import { extractErrorDetail } from '@/utils/errorHandler';

export const CalendarService = {
  /**
   * Search calendars with pagination and filtering
   */
  async search(
    query: {
      text?: string;
      page?: number;
      pageSize?: number;
      sortKey?: string;
      sortDirection?: 'asc' | 'desc';
      columnFilters?: Record<string, string[]>;
    } = {}
  ): Promise<{
    results: ContractManagementCalendarInDb[];
    result_count: number;
    filter_options: Record<string, unknown[]>;
  }> {
    try {
      const searchQuery: FilterableSearchQuery = {
        text: query.text ?? '',
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- 0 must use the fallback, not be preserved
        limit: query.pageSize || 10,
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- 0 must use the fallback, not be preserved
        offset: query.page ? (query.page - 1) * (query.pageSize || 10) : 0,
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
        sort_key: query.sortKey || 'created_at',
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
        sort_direction: query.sortDirection || 'desc',
        similarity_threshold: 0.5,
        column_filters: query.columnFilters ?? {},
      };

      const response = await searchContractManagementCalendars({ body: searchQuery });

      if (!response.data) {
        throw new Error('Failed to search calendars');
      }

      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to search calendars. Please try again.'));
    }
  },

  /**
   * Fetch a single calendar by UID
   */
  async getById(uid: string): Promise<ContractManagementCalendarInDb> {
    try {
      const response = await getContractManagementCalendar({ path: { calendar_uid: uid } });

      if (!response.data) {
        throw new Error('Failed to fetch calendar');
      }

      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to fetch calendar. Please try again.'));
    }
  },

  /**
   * Preview the (contract file, extraction) pairs a calendar currently resolves to
   */
  async preview(uid: string, query: CalendarPreviewQuery): Promise<CalendarPreviewResult> {
    try {
      const response = await previewContractManagementCalendar({
        path: { calendar_uid: uid },
        body: query,
      });

      if (!response.data) {
        throw new Error('Failed to load calendar preview');
      }

      return response.data;
    } catch (error) {
      throw new Error(
        extractErrorDetail(error, 'Failed to load calendar preview. Please try again.')
      );
    }
  },

  /**
   * Every (contract file, extraction) pair with an occurrence in `[start, end)` — unpaginated
   */
  async previewRange(uid: string, query: CalendarRangeQuery): Promise<CalendarPreviewResult> {
    try {
      const response = await previewContractManagementCalendarRange({
        path: { calendar_uid: uid },
        body: query,
      });

      if (!response.data) {
        throw new Error('Failed to load calendar preview');
      }

      return response.data;
    } catch (error) {
      throw new Error(
        extractErrorDetail(error, 'Failed to load calendar preview. Please try again.')
      );
    }
  },

  /**
   * Fetch the ICS subscription URL for a calendar
   */
  async getExportUrl(uid: string): Promise<string> {
    try {
      const response = await getContractManagementCalendarExportUrl({
        path: { calendar_uid: uid },
      });

      if (!response.data) {
        throw new Error('Failed to fetch calendar link');
      }

      return response.data;
    } catch (error) {
      throw new Error(
        extractErrorDetail(error, 'Failed to fetch calendar link. Please try again.')
      );
    }
  },

  /**
   * Regenerate the ICS subscription URL, invalidating the previous one
   */
  async regenerateExportUrl(uid: string): Promise<string> {
    try {
      const response = await regenerateContractManagementCalendarExportUrl({
        path: { calendar_uid: uid },
      });

      if (!response.data) {
        throw new Error('Failed to regenerate calendar link');
      }

      return response.data;
    } catch (error) {
      throw new Error(
        extractErrorDetail(error, 'Failed to regenerate calendar link. Please try again.')
      );
    }
  },

  /**
   * Create a new calendar
   */
  async create(
    calendarData: ContractManagementCalendarCreate
  ): Promise<ContractManagementCalendarInDb> {
    try {
      const response = await createContractManagementCalendar({ body: calendarData });

      if (!response.data) {
        throw new Error('Failed to create calendar');
      }

      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to create calendar. Please try again.'));
    }
  },

  /**
   * Update an existing calendar
   */
  async update(
    uid: string,
    updates: ContractManagementCalendarUpdate
  ): Promise<ContractManagementCalendarInDb> {
    try {
      const response = await updateContractManagementCalendar({
        path: { calendar_uid: uid },
        body: updates,
      });

      if (!response.data) {
        throw new Error('Failed to update calendar');
      }

      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to update calendar. Please try again.'));
    }
  },

  /**
   * Delete a calendar
   */
  async delete(uid: string): Promise<void> {
    try {
      await deleteContractManagementCalendar({ path: { calendar_uid: uid } });
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to delete calendar. Please try again.'));
    }
  },

  // ==================== Receivers ====================

  async listReceivers(uid: string): Promise<UserInDb[]> {
    try {
      const response = await listCalendarReceivers({ path: { calendar_uid: uid } });
      return response.data ?? [];
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to load receivers. Please try again.'));
    }
  },

  async addReceivers(uid: string, uids: string[]): Promise<ContractManagementCalendarLinkResult> {
    try {
      const response = await addCalendarReceivers({ path: { calendar_uid: uid }, body: { uids } });
      if (!response.data) throw new Error('Failed to add receivers');
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to add receivers. Please try again.'));
    }
  },

  async removeReceivers(
    uid: string,
    uids: string[]
  ): Promise<ContractManagementCalendarLinkResult> {
    try {
      const response = await removeCalendarReceivers({
        path: { calendar_uid: uid },
        body: { uids },
      });
      if (!response.data) throw new Error('Failed to remove receivers');
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to remove receivers. Please try again.'));
    }
  },

  // ==================== Permission Users ====================

  async listPermissionUsers(uid: string): Promise<UserInDb[]> {
    try {
      const response = await listCalendarPermissionUsers({ path: { calendar_uid: uid } });
      return response.data ?? [];
    } catch (error) {
      throw new Error(
        extractErrorDetail(error, 'Failed to load permission users. Please try again.')
      );
    }
  },

  async addPermissionUsers(
    uid: string,
    uids: string[]
  ): Promise<ContractManagementCalendarLinkResult> {
    try {
      const response = await addCalendarPermissionUsers({
        path: { calendar_uid: uid },
        body: { uids },
      });
      if (!response.data) throw new Error('Failed to add permission users');
      return response.data;
    } catch (error) {
      throw new Error(
        extractErrorDetail(error, 'Failed to add permission users. Please try again.')
      );
    }
  },

  async removePermissionUsers(
    uid: string,
    uids: string[]
  ): Promise<ContractManagementCalendarLinkResult> {
    try {
      const response = await removeCalendarPermissionUsers({
        path: { calendar_uid: uid },
        body: { uids },
      });
      if (!response.data) throw new Error('Failed to remove permission users');
      return response.data;
    } catch (error) {
      throw new Error(
        extractErrorDetail(error, 'Failed to remove permission users. Please try again.')
      );
    }
  },

  // ==================== Permission Roles ====================

  async listPermissionRoles(uid: string): Promise<PermissionRoleInDb[]> {
    try {
      const response = await listCalendarPermissionRoles({ path: { calendar_uid: uid } });
      return response.data ?? [];
    } catch (error) {
      throw new Error(
        extractErrorDetail(error, 'Failed to load permission roles. Please try again.')
      );
    }
  },

  async addPermissionRoles(
    uid: string,
    uids: string[]
  ): Promise<ContractManagementCalendarLinkResult> {
    try {
      const response = await addCalendarPermissionRoles({
        path: { calendar_uid: uid },
        body: { uids },
      });
      if (!response.data) throw new Error('Failed to add permission roles');
      return response.data;
    } catch (error) {
      throw new Error(
        extractErrorDetail(error, 'Failed to add permission roles. Please try again.')
      );
    }
  },

  async removePermissionRoles(
    uid: string,
    uids: string[]
  ): Promise<ContractManagementCalendarLinkResult> {
    try {
      const response = await removeCalendarPermissionRoles({
        path: { calendar_uid: uid },
        body: { uids },
      });
      if (!response.data) throw new Error('Failed to remove permission roles');
      return response.data;
    } catch (error) {
      throw new Error(
        extractErrorDetail(error, 'Failed to remove permission roles. Please try again.')
      );
    }
  },

  // ==================== Source Directories ====================

  async listSourceDirectories(uid: string): Promise<VaultDirectoryInDbSlim[]> {
    try {
      const response = await listCalendarSourceDirectories({ path: { calendar_uid: uid } });
      return response.data ?? [];
    } catch (error) {
      throw new Error(
        extractErrorDetail(error, 'Failed to load source folders. Please try again.')
      );
    }
  },

  async addSourceDirectories(
    uid: string,
    uids: string[]
  ): Promise<ContractManagementCalendarLinkResult> {
    try {
      const response = await addCalendarSourceDirectories({
        path: { calendar_uid: uid },
        body: { uids },
      });
      if (!response.data) throw new Error('Failed to add source folders');
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to add source folders. Please try again.'));
    }
  },

  async removeSourceDirectories(
    uid: string,
    uids: string[]
  ): Promise<ContractManagementCalendarLinkResult> {
    try {
      const response = await removeCalendarSourceDirectories({
        path: { calendar_uid: uid },
        body: { uids },
      });
      if (!response.data) throw new Error('Failed to remove source folders');
      return response.data;
    } catch (error) {
      throw new Error(
        extractErrorDetail(error, 'Failed to remove source folders. Please try again.')
      );
    }
  },

  // ==================== Source Contracts ====================

  async listSourceContracts(uid: string): Promise<ContractManagementContractInDbSlim[]> {
    try {
      const response = await listCalendarSourceContracts({ path: { calendar_uid: uid } });
      return response.data ?? [];
    } catch (error) {
      throw new Error(
        extractErrorDetail(error, 'Failed to load source contracts. Please try again.')
      );
    }
  },

  async addSourceContracts(
    uid: string,
    uids: string[]
  ): Promise<ContractManagementCalendarLinkResult> {
    try {
      const response = await addCalendarSourceContracts({
        path: { calendar_uid: uid },
        body: { uids },
      });
      if (!response.data) throw new Error('Failed to add source contracts');
      return response.data;
    } catch (error) {
      throw new Error(
        extractErrorDetail(error, 'Failed to add source contracts. Please try again.')
      );
    }
  },

  async removeSourceContracts(
    uid: string,
    uids: string[]
  ): Promise<ContractManagementCalendarLinkResult> {
    try {
      const response = await removeCalendarSourceContracts({
        path: { calendar_uid: uid },
        body: { uids },
      });
      if (!response.data) throw new Error('Failed to remove source contracts');
      return response.data;
    } catch (error) {
      throw new Error(
        extractErrorDetail(error, 'Failed to remove source contracts. Please try again.')
      );
    }
  },
};
